import { useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

export default function AdminBets({ onChanged }) {
  const [bets, setBets] = useState([]);
  const [settling, setSettling] = useState(null);
  const [winner, setWinner] = useState("");

  const load = () => api.get("/admin/bets").then((r) => setBets(r.data.items));
  useEffect(() => { load(); }, []);

  const markets = Object.values(
    bets.filter((b) => b.status === "open").reduce((acc, b) => {
      const g = (acc[b.match_id] ||= { match_id: b.match_id, name: b.match_name, selections: new Set(), count: 0, exposure: 0 });
      g.selections.add(b.selection); g.count += 1; g.exposure += b.liability;
      return acc;
    }, {})
  );

  const openSettle = async (mk) => {
    let runners = [...mk.selections];
    try {
      const { data } = await api.get(`/cricket/matches/${mk.match_id}`);
      runners = [...new Set([...(data.match.book || []).map((b) => b.selection), ...runners])];
    } catch {
      // match no longer listed by the provider; fall back to selections that have bets
    }
    setWinner("");
    setSettling({ ...mk, selections: runners });
  };

  const settle = async (voidIt) => {
    try {
      const { data } = await api.post(`/admin/matches/${settling.match_id}/settle`, voidIt ? { void: true } : { winner });
      toast.success(`Settled ${data.settled} ${data.settled === 1 ? "bet" : "bets"}`);
      setSettling(null); setWinner("");
      load(); onChanged?.();
    } catch (e) { toast.error(formatError(e)); }
  };

  return (
    <div className="space-y-6">
      <Card className="bg-zinc-950 border-amber-500/20">
        <CardContent className="p-0">
          <div className="px-4 py-2 bg-zinc-900 font-display uppercase tracking-widest text-amber-400 text-sm">Open Markets</div>
          {markets.length === 0 && <div className="p-6 text-zinc-500 text-sm">No open bets.</div>}
          {markets.map((mk) => (
            <div key={mk.match_id} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-zinc-800" data-testid={`market-${mk.match_id}`}>
              <div>
                <div className="font-semibold">{mk.name}</div>
                <div className="text-xs text-zinc-400">{mk.count} bets · exposure <span className="font-mono-stat text-amber-300">{mk.exposure.toFixed(2)}</span></div>
              </div>
              <Button size="sm" onClick={() => openSettle(mk)} data-testid={`settle-${mk.match_id}`} className="bg-amber-400 text-black font-display uppercase tracking-widest">Settle</Button>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card className="bg-zinc-950 border-amber-500/20">
        <CardContent className="p-0 overflow-x-auto">
          <div className="px-4 py-2 bg-zinc-900 font-display uppercase tracking-widest text-amber-400 text-sm">All Bets</div>
          {bets.map((b) => (
            <div key={b.id} className="grid grid-cols-12 gap-2 px-4 py-2 border-b border-zinc-800 text-sm items-center">
              <div className="col-span-3 font-mono-stat text-xs truncate">{b.user_email}</div>
              <div className="col-span-4 truncate">{b.selection} <span className="text-zinc-500 text-xs">· {b.match_name}</span></div>
              <div className="col-span-2 text-xs"><span className="uppercase font-bold" style={{ color: b.side === "back" ? "#72BBEF" : "#FAA9BA" }}>{b.side}</span> @{b.price.toFixed(2)}</div>
              <div className="col-span-2 font-mono-stat text-xs">{b.stake.toFixed(2)}</div>
              <div className="col-span-1"><Badge className="bg-zinc-800 text-amber-300">{b.status}</Badge></div>
            </div>
          ))}
        </CardContent>
      </Card>

      <Dialog open={!!settling} onOpenChange={(o) => !o && setSettling(null)}>
        <DialogContent className="bg-zinc-950 border-amber-500/20">
          <DialogHeader><DialogTitle className="font-display gold-text">Settle · {settling?.name}</DialogTitle></DialogHeader>
          <div className="text-xs text-zinc-400">Pick the winning selection, or void the market to refund every bet.</div>
          <div className="flex flex-col gap-2">
            {[...(settling?.selections || [])].map((s) => (
              <button key={s} onClick={() => setWinner(s)} data-testid={`winner-${s}`}
                className={`text-left px-3 py-2 rounded border ${winner === s ? "border-amber-400 bg-amber-400/10 text-amber-300" : "border-zinc-800"}`}>
                {s}
              </button>
            ))}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => settle(true)} data-testid="settle-void" className="border-zinc-600">Void (refund all)</Button>
            <Button disabled={!winner} onClick={() => settle(false)} data-testid="settle-confirm" className="bg-amber-400 text-black font-display uppercase tracking-widest">Settle</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
